import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';

export default function RequireOnboarding() {
    const { session, loading, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [checkingStudio, setCheckingStudio] = useState(false);
    const hasChecked = useRef(false);

    // SECURITY NOTE: This client-side validation is for UX only.
    // Server-side middleware on all protected API routes is the actual security enforcement.
    // Users can bypass this component, but they cannot bypass server-side validation.
    // If a user hasn't completed onboarding, they'll get 404 responses from the API.

    useEffect(() => {
        async function checkStudio() {
            // Don't check if auth is still loading or no session
            if (loading || !session?.access_token) {
                return;
            }

            // Don't re-check if we already checked successfully
            if (hasChecked.current) {
                return;
            }

            // If we're coming from signup, allow access to onboarding
            if (location.state?.from === 'signup') {
                hasChecked.current = true;
                return;
            }

            // Check if user has completed payment (account_status should be 'active')
            // Only check for main accounts (sub-accounts don't have user_metadata)
            if (user && 'user_metadata' in user) {
                const accountStatus = user.user_metadata?.account_status;
                if (accountStatus === 'pending_payment' || accountStatus === 'pending_onboarding') {
                    // User hasn't completed onboarding yet
                    if (location.pathname !== '/app/onboarding') {
                        navigate('/app/onboarding');
                        return;
                    }
                    hasChecked.current = true;
                    return;
                }
            }

            setCheckingStudio(true);
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
                    {
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (res.ok) {
                    const studio = await res.json();

                    // Check if user has completed onboarding and has active subscription
                    if (studio && studio.is_active) {
                        // User has completed onboarding and has active subscription
                        // Redirect to dashboard if they're not already there
                        if (!window.location.pathname.includes('/dashboard')) {
                            navigate('/app/dashboard');
                        }
                        hasChecked.current = true;
                        return;
                    }

                    // Studio exists but is inactive - redirect to inactive studio flow
                    if (studio && !studio.is_active) {
                        navigate('/app/inactive-studio/settings/billing');
                        return;
                    }
                } else {
                    // Handle server-side onboarding validation failures
                    if (res.status === 404) {
                        // Studio not found - user needs to complete onboarding
                        if (location.pathname !== '/app/onboarding') {
                            navigate('/app/onboarding');
                            return;
                        }
                    }
                }

                // If no studio, redirect to onboarding
                if (location.pathname !== '/app/onboarding') {
                    navigate('/app/onboarding');
                    return;
                }

                hasChecked.current = true;
            } catch (error) {
                // On error, redirect to onboarding to be safe
                if (location.pathname !== '/app/onboarding') {
                    navigate('/app/onboarding');
                }
                hasChecked.current = true;
            } finally {
                setCheckingStudio(false);
            }
        }

        checkStudio();
    }, [session, loading, navigate, location]);

    // Show loading only during the studio check, not during auth loading
    if (checkingStudio) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return <Outlet />;
}
